import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Scenario, Session } from "../types";
import { listMyDrafts } from "../services/scenarioAdminService";
import { usePermissionLevel } from "../hooks/usePermissionLevel";
import AssignRubricModal from "./AssignRubricModal";
import { categoryStyle } from "../utils/categoryStyles";
import { hasSessionForScenario } from "../utils/sessionLookup";

type ScenarioSummary = Pick<Scenario, 'id' | 'title' | 'description' | 'category'> & {
  canEdit: boolean;
  // The caller's EFFECTIVE rubric, resolved server-side: a manager's own
  // override where they set one, otherwise the scenario's default (which is
  // what an admin always sees, since that is the value they control).
  // Absent entirely — not null — for members, who see no rubric at all.
  rubricId?: string | null;
  rubricName?: string | null;
  hero?: string; // signed GCS URL for the wide key art; short-lived, may 403
};
type DraftSummary = ScenarioSummary & { status?: "draft" | "published" };

/**
 * The h-36 banner shared by both the draft tile and the published card: hero
 * art behind the grid texture (texture only when there's no art or it failed
 * to load), then the bottom scrim, then whatever the caller overlays (badge,
 * icon cluster). `hero` is a short-lived signed GCS URL, so a stale one can
 * 403 after the library has been open a while — that falls back to the same
 * texture as "never had art" rather than a broken-image box, the same pattern
 * ScenarioBriefingPage and SessionToolColumn use for their own hero art.
 */
const CardBanner: React.FC<{
  hero?: string;
  gradient: string;
  children?: React.ReactNode;
}> = ({ hero, gradient, children }) => {
  const [heroFailed, setHeroFailed] = useState(false);
  useEffect(() => {
    setHeroFailed(false);
  }, [hero]);
  const showHero = !!hero && !heroFailed;

  return (
    <div className={`h-36 relative bg-gradient-to-br ${gradient} bg-surface-container-highest`}>
      {showHero ? (
        <img
          src={hero}
          alt=""
          onError={() => setHeroFailed(true)}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg,transparent,transparent 23px,currentColor 23px,currentColor 24px),repeating-linear-gradient(90deg,transparent,transparent 23px,currentColor 23px,currentColor 24px)",
          }}
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-surface-container-high to-transparent" />
      {children}
    </div>
  );
};

// Focus ring shared with ScenarioBriefingPage: the card body is a real
// keyboard target now, so it needs a visible one.
const FOCUS_RING =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-tertiary";

interface LibraryPageProps {
  sessions: Session[];
  getToken: () => string | undefined;
  /**
   * Opens the scenario's briefing page. Reached from the card body for every
   * scenario, and from the CTA for one the player has not started — the
   * briefing is the scenario's info page, not only the gate in front of a
   * first session.
   */
  onOpenBriefing: (scenarioId: string) => void;
  onResumeScenario: (scenarioId: string) => void;
  onOpenCreate: () => void;
  onResumeDraft: (scenarioId: string) => void;
  onEditScenario: (scenarioId: string) => void;
}

const LibraryPage: React.FC<LibraryPageProps> = ({
  sessions,
  getToken,
  onOpenBriefing,
  onResumeScenario,
  onOpenCreate,
  onResumeDraft,
  onEditScenario,
}) => {
  const { isManagerOrAbove } = usePermissionLevel();
  const { orgSlug } = useParams<{ orgSlug: string }>();

  const [scenarios, setScenarios] = useState<ScenarioSummary[]>([]);
  const [assignTarget, setAssignTarget] = useState<ScenarioSummary | null>(null);
  const [drafts, setDrafts] = useState<DraftSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const { getScenarioLibrary } = await import("../utils/apiUtil");
        const token = getToken();
        const draftsPromise = isManagerOrAbove
          ? listMyDrafts(token).catch((e) => {
              console.error("Failed to load drafts", e);
              return [] as DraftSummary[];
            })
          : Promise.resolve([] as DraftSummary[]);
        const [publicLib, myDrafts] = await Promise.all([
          getScenarioLibrary(token),
          draftsPromise,
        ]);
        setScenarios(publicLib);
        setDrafts(myDrafts);
      } catch (e: any) {
        setError(e.message ?? "Failed to load library");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isManagerOrAbove]);

  const hasSession = (scenarioId: string) =>
    hasSessionForScenario(sessions, scenarioId);

  // Both callbacks are synchronous route changes — onOpenBriefing to the
  // briefing page, onResumeScenario straight to chat — so there is no gap for
  // a busy/spinner state to ever be visible.
  //
  // The CTA keeps its old split: an unplayed scenario goes through the
  // briefing gate, a started one resumes where the player left off. The card
  // body around it always opens the briefing, which is how someone mid-
  // scenario can still go back and re-read their role and the cast.
  const handleCta = (scenario: ScenarioSummary) => {
    if (hasSession(scenario.id)) {
      onResumeScenario(scenario.id);
    } else {
      onOpenBriefing(scenario.id);
    }
  };

  return (
    <div className="flex flex-col flex-1 h-full min-w-0 overflow-y-auto bg-background">
      <div className="max-w-5xl w-full mx-auto px-8 py-10">

        {/* Page header */}
        <div className="mb-8">
          <h2 className="font-headline text-3xl font-bold tracking-tight text-on-surface mb-1">
            Scenario Library
          </h2>
          <p className="text-slate-500 font-label text-xs uppercase tracking-widest">
            Select a scenario to begin or resume a scenario
          </p>
        </div>

        {/* States */}
        {loading && (
          <div className="flex items-center justify-center py-24 text-slate-500 font-label text-sm uppercase tracking-widest animate-pulse">
            Loading library...
          </div>
        )}

        {!loading && error && (
          <div className="flex flex-col items-center justify-center py-24 gap-3 text-error">
            <span className="material-symbols-outlined text-4xl">warning</span>
            <p className="font-label text-sm uppercase tracking-widest">{error}</p>
          </div>
        )}

        {/* Scenario grid */}
        {!loading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {/* Create-Scenario tile — manager-or-above entry point to scenario authoring. */}
            {isManagerOrAbove && (
              <button
                type="button"
                onClick={onOpenCreate}
                className="flex flex-col items-center justify-center min-h-[260px] rounded-lg border-2 border-dashed border-outline-variant/40 bg-surface-container/40 hover:border-primary hover:bg-surface-container hover:text-primary transition-colors text-slate-400 group"
              >
                <span className="material-symbols-outlined text-5xl text-primary/60 group-hover:text-primary">
                  add
                </span>
                <span className="mt-2 font-label text-xs uppercase tracking-widest font-bold">
                  Create Scenario
                </span>
              </button>
            )}

            {/* Drafts (owner-scoped) — render next, visually distinct */}
            {isManagerOrAbove && drafts.map((draft) => {
              const style = categoryStyle(draft.category);
              return (
                <div
                  key={`draft-${draft.id}`}
                  className="group flex flex-col bg-surface-container-high rounded-lg overflow-hidden border-l-2 border-tertiary transition-all duration-200 hover:bg-surface-container-highest"
                >
                  <CardBanner hero={draft.hero} gradient={style.gradient}>
                    <div className="absolute top-4 left-4">
                      <span className="text-[10px] font-label font-bold uppercase tracking-widest px-2 py-1 rounded border backdrop-blur-sm bg-tertiary/10 text-tertiary border-tertiary/20">
                        Draft
                      </span>
                    </div>
                  </CardBanner>
                  <div className="flex flex-col flex-1 p-5">
                    <h3 className="font-headline text-lg font-bold text-on-surface mb-3 leading-tight">
                      {draft.title || "(Untitled draft)"}
                    </h3>
                    {draft.description && (
                      <p className="text-slate-500 text-xs font-body leading-relaxed mb-4 line-clamp-2">
                        {draft.description}
                      </p>
                    )}
                    <button
                      onClick={() => onResumeDraft(draft.id)}
                      className="mt-auto w-full py-2.5 rounded font-label font-bold text-xs uppercase tracking-widest transition-all duration-200 border bg-surface-container-lowest text-tertiary border-tertiary/30 hover:bg-tertiary hover:text-on-primary"
                    >
                      Resume Editing
                    </button>
                  </div>
                </div>
              );
            })}

            {/* Published scenarios */}
            {scenarios.map((scenario) => {
              const style = categoryStyle(scenario.category);
              const resume = hasSession(scenario.id);
              // Server-computed: canWriteScenario(row, caller) in
              // content-access.ts, the same predicate every write on this
              // branch routes through. Not derived client-side — there is no
              // reliable way to map the Stytch session to the server's
              // internal users.id on this page.
              const editable = scenario.canEdit;

              return (
                // Clicking anywhere on the card opens the briefing. The div
                // carries no role: the controls nested inside it (title,
                // rubric, edit, CTA) are the real interactive elements, and
                // the title button below is the keyboard route to the same
                // place — so this is a convenience target for pointers, not
                // an announced one. Every nested control stops propagation
                // so it wins over the card underneath it.
                <div
                  key={scenario.id}
                  onClick={() => onOpenBriefing(scenario.id)}
                  className={`
                    group flex flex-col bg-surface-container-high rounded-lg overflow-hidden
                    border-l-2 transition-all duration-200 cursor-pointer
                    ${resume ? style.border : "border-transparent hover:" + style.border}
                    hover:bg-surface-container-highest
                  `}
                >
                  {/* Banner */}
                  <CardBanner hero={scenario.hero} gradient={style.gradient}>
                    {/* Category badge */}
                    <div className="absolute top-4 left-4">
                      <span
                        className={`text-[10px] font-label font-bold uppercase tracking-widest px-2 py-1 rounded border backdrop-blur-sm ${style.badge}`}
                      >
                        {scenario.category}
                      </span>
                    </div>

                    {/* Right-side icon cluster: resume indicator + admin edit affordance */}
                    {(resume || editable) && (
                      <div className="absolute top-4 right-4 flex items-center gap-1.5">
                        {resume && (
                          <span className="material-symbols-outlined text-primary text-base opacity-80" title="Session in progress">
                            play_circle
                          </span>
                        )}
                        {editable && (
                          <button
                            type="button"
                            title="Edit scenario"
                            onClick={(e) => { e.stopPropagation(); onEditScenario(scenario.id); }}
                            className="inline-flex items-center justify-center w-7 h-7 rounded backdrop-blur-sm bg-surface-container/60 text-slate-400 hover:bg-primary hover:text-on-primary transition-colors border border-outline-variant/20"
                          >
                            <span className="material-symbols-outlined text-base">edit</span>
                          </button>
                        )}
                      </div>
                    )}
                  </CardBanner>

                  {/* Card body */}
                  <div className="flex flex-col flex-1 p-5">
                    <h3 className="font-headline text-lg font-bold text-on-surface mb-3 leading-tight">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenBriefing(scenario.id);
                        }}
                        className={`text-left rounded-sm transition-colors duration-200 motion-reduce:transition-none group-hover:text-primary ${FOCUS_RING}`}
                      >
                        {scenario.title}
                      </button>
                    </h3>

                    {scenario.description && (
                      <p className="text-slate-500 text-xs font-body leading-relaxed mb-4 line-clamp-2">
                        {scenario.description}
                      </p>
                    )}

                    {/* Manager-or-above: assign / show the grading rubric for this
                        scenario. Members must not see that a rubric exists at all. */}
                    {isManagerOrAbove && (
                      <div className="mb-3">
                        <button
                          type="button"
                          className="flex items-center gap-1 max-w-full text-xs text-slate-300 hover:text-on-surface"
                          onClick={(e) => {
                            e.stopPropagation();
                            setAssignTarget(scenario);
                          }}
                          title={scenario.rubricName ? `Rubric: ${scenario.rubricName}` : "Assign rubric"}
                        >
                          <span className="material-symbols-outlined text-sm leading-none" style={{ fontSize: "16px" }}>rule</span>
                          <span className="truncate min-w-0">{scenario.rubricName ?? "Assign rubric"}</span>
                        </button>
                      </div>
                    )}

                    {/* CTA */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCta(scenario);
                      }}
                      className={`
                        mt-auto w-full py-2.5 rounded font-label font-bold text-xs uppercase tracking-widest
                        transition-all duration-200 border
                        ${resume
                          ? "bg-primary text-on-primary border-transparent hover:opacity-90 active:opacity-80"
                          : "bg-surface-container-lowest text-primary border-primary/20 hover:bg-primary hover:text-on-primary group-hover:border-primary"
                        }
                      `}
                    >
                      {resume ? "Resume Scenario" : "Begin Scenario"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {assignTarget && (
        <AssignRubricModal
          scenarioId={assignTarget.id}
          scenarioTitle={assignTarget.title}
          // The effective rubric for THIS caller, read from live state rather
          // than the snapshot captured when the card was clicked, so the
          // control opens on what is actually in force — including a manager's
          // own override of a global scenario, which used to show the global
          // default and make their previous save look like it never happened.
          currentRubricId={
            (scenarios.find((s) => s.id === assignTarget.id) ?? assignTarget)
              .rubricId ?? null
          }
          orgSlug={orgSlug ?? ""}
          getToken={getToken}
          onClose={() => setAssignTarget(null)}
          // Optimistic patch. Safe now that the list endpoint returns the
          // caller's effective rubric: a refetch confirms this value instead
          // of reverting it to the scenario default.
          onSaved={(rubricId, rubricName) => {
            setScenarios((prev) =>
              prev.map((s) =>
                s.id === assignTarget.id ? { ...s, rubricId, rubricName } : s,
              ),
            );
            setAssignTarget(null);
          }}
        />
      )}
    </div>
  );
};

export default LibraryPage;
